export interface DiaryTimeZoneOption {
  description: string;
  /** Standard hours behind UTC. This is the dropdown value. */
  value: number;
  windowsId: string;
}

/** Legacy diary zones. Option values are standard offsets; Hawaii does not change for daylight time. */
export const DIARY_TIME_ZONE_OPTIONS: DiaryTimeZoneOption[] = [
  {
    description: 'ET-Eastern Time',
    value: 5,
    windowsId: 'Eastern Standard Time',
  },
  {
    description: 'CT-Central Time',
    value: 6,
    windowsId: 'Central Standard Time',
  },
  {
    description: 'MT-Mountain Time',
    value: 7,
    windowsId: 'Mountain Standard Time',
  },
  {
    description: 'PT-Pacific Time',
    value: 8,
    windowsId: 'Pacific Standard Time',
  },
  {
    description: 'AKT-Alaska Time',
    value: 9,
    windowsId: 'Alaskan Standard Time',
  },
  {
    description: 'HAT-Hawaii Time',
    value: 10,
    windowsId: 'Hawaiian Standard Time',
  },
];

const CENTRAL_ZONE_VALUE = 6;

export interface DiaryWallClock {
  hour24: number;
  minute: number;
  zoneValue: number;
  abbreviation: string;
}

/**
 * Hours behind UTC for US Central on the due date.
 * 5 during daylight time, 6 otherwise. The browser zone is not used.
 */
export function centralOffsetForDueDate(
  dueDate: Date | string | null | undefined,
): number {
  const { year, month, day } = calendarParts(dueDate);
  const probe = new Date(Date.UTC(year, month - 1, day, 17, 0, 0));
  return timeZoneOffsetHours('America/Chicago', probe) === 5 ? 5 : 6;
}

/** Positive hours behind UTC. Existing rows stored the browser offset as a negative number. */
export function normalizeTimeOffset(
  timeOffset: number | string | null | undefined,
): number | null {
  if (timeOffset == null || timeOffset === '') {
    return null;
  }
  const value = Number(timeOffset);
  if (!Number.isFinite(value)) {
    return null;
  }
  return Math.abs(Math.round(value));
}

/** Offset persisted for the selected zone on the due date. */
export function storedOffsetForZone(
  zoneValue: number | null | undefined,
  dueDate: Date | string | null | undefined,
): number {
  const standard = Number(zoneValue);
  if (standard === 10) {
    return 10;
  }
  const zone = DIARY_TIME_ZONE_OPTIONS.some((item) => item.value === standard)
    ? standard
    : CENTRAL_ZONE_VALUE;
  return centralOffsetForDueDate(dueDate) === 5 ? zone - 1 : zone;
}

/**
 * New-diary default. Uses the browser offset and the browser daylight-time flag,
 * matching Legacy: Mexico City (UTC−6, no daylight time) selects CT.
 */
export function defaultDiaryZoneValue(now = new Date()): number {
  const offset = Math.round(now.getTimezoneOffset() / 60);
  const january = new Date(now.getFullYear(), 0, 1).getTimezoneOffset();
  const july = new Date(now.getFullYear(), 6, 1).getTimezoneOffset();
  const daylight = now.getTimezoneOffset() < Math.max(january, july);
  const zoneValue = daylight ? offset + 1 : offset;
  return DIARY_TIME_ZONE_OPTIONS.some((item) => item.value === zoneValue)
    ? zoneValue
    : CENTRAL_ZONE_VALUE;
}

export function windowsZoneId(zoneValue: number | null | undefined): string {
  const standard = Number(zoneValue);
  return (
    DIARY_TIME_ZONE_OPTIONS.find((item) => item.value === standard)?.windowsId ??
    'Central Standard Time'
  );
}

/**
 * Dropdown value for a stored offset.
 * During US Central daylight time the stored value is one less than the option, except Hawaii (10).
 */
export function zoneValueForOffset(
  timeOffset: number | string | null | undefined,
  dueDate: Date | string | null | undefined,
): number {
  const offset = normalizeTimeOffset(timeOffset);
  if (offset == null || offset === 10) {
    return offset === 10 ? 10 : CENTRAL_ZONE_VALUE;
  }
  const standard =
    centralOffsetForDueDate(dueDate) === 5 ? offset + 1 : offset;
  return DIARY_TIME_ZONE_OPTIONS.some((item) => item.value === standard)
    ? standard
    : CENTRAL_ZONE_VALUE;
}

export function abbreviationForOffset(
  timeOffset: number | string | null | undefined,
  dueDate: Date | string | null | undefined,
): string {
  const offset = normalizeTimeOffset(timeOffset);
  const daylight = centralOffsetForDueDate(dueDate) === 5;
  switch (offset) {
    case 4:
      return 'ET';
    case 5:
      return daylight ? 'CT' : 'ET';
    case 6:
      return daylight ? 'MT' : 'CT';
    case 7:
      return daylight ? 'PT' : 'MT';
    case 8:
      return daylight ? 'AKT' : 'PT';
    case 9:
      return 'AKT';
    case 10:
      return 'HAT';
    default:
      return 'CT';
  }
}

/**
 * Central clock shifted into the saved zone.
 * Hour and minute come from `centralTime` in America/Chicago.
 * `hours -= (timeOffset - centralOffset)`, then wrapped into 0–23.
 */
export function wallClockFromCentral(
  centralTime: Date | string | null | undefined,
  timeOffset: number | string | null | undefined,
  dueDate: Date | string | null | undefined,
): DiaryWallClock {
  const offset = normalizeTimeOffset(timeOffset);
  const central = centralClock(centralTime);
  let hour24 = central.hour24;
  if (offset != null) {
    hour24 -= offset - centralOffsetForDueDate(dueDate);
  }
  hour24 = mod24(hour24);
  return {
    hour24,
    minute: central.minute,
    zoneValue: zoneValueForOffset(offset, dueDate),
    abbreviation: abbreviationForOffset(offset, dueDate),
  };
}

/** Search and edit label. Missing reminder time stays the Legacy default. */
export function formatDiaryNotificationTime(diary: {
  emailReminder?: boolean;
  hours?: number | string | null;
  minutes?: number | string | null;
  timeOffset?: number | string | null;
  dueDate?: Date | string | null;
  dateDue?: Date | string | null;
  notificationTime?: unknown;
}): string {
  if (!diary.emailReminder) {
    return '';
  }
  const hasClock = diary.hours != null && diary.minutes != null;
  const hasInstant = diary.notificationTime != null && diary.notificationTime !== '';
  if (!hasClock || !hasInstant) {
    return '3:00 PM CT';
  }
  const centralTime = diary.notificationTime;
  const wall = wallClockFromCentral(
    centralTime instanceof Date || typeof centralTime === 'string'
      ? centralTime
      : null,
    diary.timeOffset,
    diary.dateDue ?? diary.dueDate,
  );
  return formatWallClock(wall);
}

export function formatWallClock(wall: DiaryWallClock): string {
  const suffix = wall.hour24 >= 12 ? 'PM' : 'AM';
  let hour = wall.hour24 % 12;
  if (hour === 0) {
    hour = 12;
  }
  const minute = String(wall.minute).padStart(2, '0');
  return `${hour}:${minute} ${suffix} ${wall.abbreviation}`;
}

function mod24(hours: number): number {
  return ((Math.round(hours) % 24) + 24) % 24;
}

function centralClock(value: Date | string | null | undefined): {
  hour24: number;
  minute: number;
} {
  const instant =
    value instanceof Date
      ? value
      : typeof value === 'string' && value
        ? new Date(value)
        : null;
  if (!instant || isNaN(instant.getTime())) {
    return { hour24: 15, minute: 0 };
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  const hour24 = pick('hour');
  const minute = pick('minute');
  return {
    hour24: Number.isFinite(hour24) ? hour24 % 24 : 15,
    minute: Number.isFinite(minute) ? ((minute % 60) + 60) % 60 : 0,
  };
}

function calendarParts(value: Date | string | null | undefined): {
  year: number;
  month: number;
  day: number;
} {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    };
  }
  if (typeof value === 'string') {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) {
      return { year: +iso[1], month: +iso[2], day: +iso[3] };
    }
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return {
        year: parsed.getFullYear(),
        month: parsed.getMonth() + 1,
        day: parsed.getDate(),
      };
    }
  }
  const today = new Date();
  return {
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    day: today.getDate(),
  };
}

function timeZoneOffsetHours(timeZone: string, instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  const asUtc = Date.UTC(
    pick('year'),
    pick('month') - 1,
    pick('day'),
    pick('hour') % 24,
    pick('minute'),
    pick('second'),
  );
  return Math.round((instant.getTime() - asUtc) / 3600000);
}
